<template>
    <div>
        <v-navigation-drawer
            id="core-navigation-drawer"
            class="main-nav-drawer"
            v-model="_drawer"
            :expand-on-hover="expandOnHover"
            :right="$vuetify.rtl"
            mobile-breakpoint="960"
            app
            width="230"
            v-bind="$attrs"
        >
            <div class="logo-container">
                <router-link to="/planners" class="logo brand-lockup">
                    <div class="brand-mark">BCI</div>
                    <div class="brand-copy">
                        <div class="brand-title">Reports</div>
                        <div class="brand-subtitle">by Ranes</div>
                    </div>
                </router-link>
            </div>
            <div style="border-bottom: 2px solid #ff302f"></div>

            <v-list dense expand nav>
                <div />
                <v-subheader class="nav-section-header">BCI</v-subheader>
                <template v-for="(item, i) in bciItems">
                    <item :key="`bci-${i}`" :item="item" />
                </template>
                <v-subheader class="nav-section-header mt-2">Portal</v-subheader>
                <template v-for="(item, i) in portalItems">
                    <item :key="`portal-${i}`" :item="item" />
                </template>
                <v-subheader class="nav-section-header mt-2">Resources</v-subheader>
                <template v-for="(item, i) in resourceItems">
                    <item :key="`resource-${i}`" :item="item" />
                </template>
                <div />
            </v-list>

            <template v-slot:append>
                <v-list expand nav>
                    <v-list-item
                        :class="`nav-link ${!$vuetify.theme.dark ? 'black' : 'white'}--text`"
                        :active-class="`grey ${!$vuetify.theme.dark ? 'lighten-2 black' : 'darken-3 white'}--text`"
                        @click="handleSystemInformationDialog"
                    >
                        <v-list-item-icon>
                            <v-icon>mdi-information-outline</v-icon>
                        </v-list-item-icon>

                        <v-list-item-content>
                            <v-list-item-title>System</v-list-item-title>
                        </v-list-item-content>
                    </v-list-item>
                </v-list>
            </template>
        </v-navigation-drawer>
        <system-information-modal v-model="showSystemInfoModal" />
    </div>
</template>

<script>
import Item from "@cmp/nav/Item.vue";
import SystemInformationModal from "@/components/modals/SystemInformationModal.vue";

export default {
    name: "DashboardCoreDrawer",

    props: ["drawer", "expandOnHover"],

    components: { Item, SystemInformationModal },

    data: () => ({
        showSystemInfoModal: false,
    }),

    computed: {
        _drawer: {
            get() {
                return this.drawer;
            },
            set(val) {
                this.$emit("update:drawer", val);
            },
        },

        portalBaseUrl() {
            const configuredUrl =
                this.$store.state.api.portalUrl || window.appConfig?.VUE_APP_PORTAL_URL || process.env.VUE_APP_PORTAL_URL;
            const baseUrl = configuredUrl || "https://portal.ranes.com/";
            return baseUrl.endsWith("/") ? baseUrl : `${baseUrl}/`;
        },

        bciItems() {
            return [
                {
                    icon: "mdi-calendar-check",
                    title: "Planners",
                    to: "/planners",
                },
                {
                    icon: "mdi-hard-hat",
                    title: "Field Mode",
                    to: "/field",
                },
            ];
        },

        portalItems() {
            return [
                {
                    icon: "mdi-file-document-edit-outline",
                    title: "Forms",
                    newTab: true,
                    href: `${this.portalBaseUrl}forms`,
                },
                {
                    icon: "mdi-camera",
                    title: "Log-it",
                    newTab: true,
                    href: `${this.portalBaseUrl}logit`,
                },
                {
                    icon: "mdi-file-document-multiple-outline",
                    title: "Documents",
                    newTab: true,
                    href: `${this.portalBaseUrl}documents`,
                },
            ];
        },

        resourceItems() {
            return [
                {
                    icon: "mdi-book-search-outline",
                    title: "Code Bank",
                    to: "/codebank",
                },
            ];
        },
    },

    methods: {
        handleSystemInformationDialog() {
            this.showSystemInfoModal = true;
        },
    },
};
</script>

<style scoped>
.main-nav-drawer {
    background: url("@assets/img/bg-main-nav-light.png");
    box-shadow: inset 0 0 0 1000px rgb(255 255 255 / 55%);
}
.main-nav-drawer .nav-link * {
    color: #222;
}

.theme--dark .main-nav-drawer {
    background: url("@assets/img/bg-main-nav-dark.png");
    box-shadow: inset 0 0 0 1000px rgb(0 0 0 / 10%);
}
.theme--dark .main-nav-drawer .nav-link * {
    color: #fff;
}

.nav-section-header {
    font-size: 0.6875rem;
    font-weight: 700;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: rgba(0, 0, 0, 0.45);
    height: 28px;
    padding: 0 16px;
}

.theme--dark .nav-section-header {
    color: rgba(255, 255, 255, 0.4);
}

.logo-container {
    padding: 16px 12px 12px;
    min-height: 88px;
    display: flex;
    align-items: center;
}

.logo {
    width: 100%;
    text-decoration: none;
}

.brand-lockup {
    display: flex;
    align-items: flex-end;
    justify-content: center;
    gap: 10px;
    color: #141414;
}

.brand-mark {
    font-family: "Baskerville", "Iowan Old Style", "Palatino Linotype", "Times New Roman", serif;
    font-size: 3.2rem;
    line-height: 0.88;
    letter-spacing: -0.12em;
    font-weight: 600;
}

.brand-copy {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    padding-bottom: 4px;
}

.brand-title {
    font-size: 1rem;
    line-height: 1;
    font-weight: 700;
    letter-spacing: 0.16em;
    text-transform: uppercase;
}

.brand-subtitle {
    margin-top: 4px;
    font-size: 0.72rem;
    line-height: 1;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    opacity: 0.72;
}

.theme--dark .brand-lockup {
    color: #ffffff;
}

.main-nav-drawer ::v-deep .v-navigation-drawer__content {
    scrollbar-width: thin;
    scrollbar-color: rgba(155, 155, 155, 0.3) transparent;
}

.main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar {
    width: 6px;
}

.main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-track {
    background: transparent;
}

.main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb {
    background-color: rgba(155, 155, 155, 0.3);
    border-radius: 3px;
}

.main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
    background-color: rgba(155, 155, 155, 0.5);
}

.theme--dark .main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb {
    background-color: rgba(200, 200, 200, 0.2);
}

.theme--dark .main-nav-drawer ::v-deep .v-navigation-drawer__content::-webkit-scrollbar-thumb:hover {
    background-color: rgba(200, 200, 200, 0.4);
}
</style>
